import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShieldCheck, GraduationCap, BarChart3, MessageCircleQuestion, ArrowRight, Eye } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import CourseQaList from '../components/instructor/CourseQaList';
import InstructorQuestionsPanel from '../components/instructor/InstructorQuestionsPanel';
import StudyGuidesTab from '../components/admin/StudyGuidesTab';
import StudyNotesTab from '../components/admin/StudyNotesTab';
import InsightsStep from '../components/programme/insights/InsightsStep';

const API = `${API_BASE}/api`;
const tabCls = 'rounded-full px-4 py-2 text-sm data-[state=active]:bg-teal-500 data-[state=active]:text-black';

export default function InstructorProgrammePage() {
  const { programmeId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [programme, setProgramme] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const isLead = myRole === 'lead';

  useEffect(() => {
    axios.get(`${API}/programmes/${programmeId}`, { headers }).then((r) => setProgramme(r.data)).catch(() => navigate('/instructor'));
    axios.get(`${API}/instructor/programmes`, { headers })
      .then((r) => setMyRole((r.data.programmes || []).find((p) => p.programme_id === programmeId)?.my_role || 'lead'))
      .catch(() => setMyRole('lead'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId]);

  const unit = programme?.structure_type === 'months' ? 'Month' : 'Week';
  return (
    <Layout pageTitle={programme?.programme_name || 'Programme'} breadcrumbItems={[{ label: 'Instructor Hub', href: '/instructor' }, { label: programme?.programme_name || 'Programme' }]}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="instructor-programme-page">
        <Button variant="ghost" size="sm" onClick={() => navigate('/instructor')} className="text-zinc-400 hover:text-white mb-6 -ml-2" data-testid="instructor-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />All my programmes
        </Button>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-400 mb-2">Instructor view</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white" data-testid="instructor-programme-title">{programme?.programme_name || '…'}</h1>
          </div>
          <Button variant="outline" onClick={() => navigate(`/programmes/${programmeId}`)} className="border-zinc-700 text-zinc-300" data-testid="instructor-view-as-learner-btn"><Eye className="h-4 w-4 mr-2" />See learner view</Button>
        </div>
        <Tabs defaultValue="qa">
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-full p-1 h-auto mb-8 flex flex-wrap w-fit">
            <TabsTrigger value="qa" className={tabCls} data-testid="instructor-tab-qa"><ShieldCheck className="h-4 w-4 mr-2" />Content QA</TabsTrigger>
            {isLead && <TabsTrigger value="marking" className={tabCls} data-testid="instructor-tab-marking"><GraduationCap className="h-4 w-4 mr-2" />Marking</TabsTrigger>}
            <TabsTrigger value="progress" className={tabCls} data-testid="instructor-tab-progress"><BarChart3 className="h-4 w-4 mr-2" />Progress</TabsTrigger>
            <TabsTrigger value="questions" className={tabCls} data-testid="instructor-tab-questions"><MessageCircleQuestion className="h-4 w-4 mr-2" />Questions</TabsTrigger>
          </TabsList>
          <TabsContent value="qa" className="space-y-10">
            <section><h2 className="text-lg text-white font-semibold mb-4">Courses</h2><CourseQaList programmeId={programmeId} token={token} /></section>
            {isLead && <section><StudyGuidesTab programmeId={programmeId} token={token} unit={unit} /></section>}
            {isLead && <section><StudyNotesTab programmeId={programmeId} token={token} /></section>}
          </TabsContent>
          <TabsContent value="marking">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-8">
              <GraduationCap className="h-10 w-10 text-emerald-400 mb-4" />
              <h2 className="text-lg text-white font-semibold mb-1">Gradebook</h2>
              <p className="text-sm text-zinc-400 mb-6 max-w-lg">Download learner submissions, give a grade out of 100 and leave feedback.</p>
              <Button onClick={() => navigate(`/instructor/programmes/${programmeId}/gradebook`)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="instructor-open-gradebook-btn">Open gradebook<ArrowRight className="h-4 w-4 ml-2" /></Button>
            </div>
          </TabsContent>
          <TabsContent value="progress"><InsightsStep programmeId={programmeId} headers={headers} /></TabsContent>
          <TabsContent value="questions"><InstructorQuestionsPanel programmeId={programmeId} token={token} /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
